/**
 * The plan document: the single shape every part of the app agrees on.
 *
 * All coordinates are integer millimetres in a per-project local system. Areas
 * printed on a listing arrive as m² floats and stay that way -- they are what
 * the estate agent claimed, not something we derived.
 *
 * Two rules hold this together, and both are load-bearing:
 *
 *  1. Walls reference shared junctions rather than carrying their own
 *     coordinates, so corners stay joined when one is dragged and openings stay
 *     attached when a wall is split.
 *  2. Rooms are derived from the wall graph, never authored. Only metadata is
 *     stored here; polygons and areas are computed (see rooms.ts).
 */

export const SCHEMA_VERSION = 1;

export interface Point {
  x: number;
  y: number;
}

export interface Junction extends Point {
  id: string;
}

export interface Wall {
  id: string;
  /** Junction id at the start of the centreline. */
  a: string;
  /** Junction id at the end of the centreline. */
  b: string;
  thickness: number;
  /** Null or absent inherits the level height. */
  height?: number | null;
  baseOffset?: number;
  /**
   * User-flagged. Nothing infers this -- it drives renovation warnings, and a
   * guess would be worse than an honest unknown.
   */
  loadBearing?: boolean;
}

export type OpeningType = 'door' | 'window' | 'opening';
export type DoorSwing = 'left-in' | 'left-out' | 'right-in' | 'right-out';

export interface Opening {
  id: string;
  /** The wall this opening is cut into. */
  wall: string;
  /** Millimetres along the wall from junction a to the opening's start. */
  offset: number;
  width: number;
  /** Height of the sill above the level's floor. Doors sit at 0. */
  sill: number;
  /** Height of the head above the level's floor. */
  head: number;
  type: OpeningType;
  swing?: DoorSwing;
}

export interface RoomMeta {
  id: string;
  /**
   * An interior point that identifies which face this metadata belongs to.
   * After an edit the faces are recomputed and each claims the room whose seed
   * falls inside it, so labels survive anything that does not restructure the
   * topology.
   */
  seed: Point;
  name?: string;
  function?: string;
  /** Area printed on the source plan, in m². Used for calibration and checking. */
  declaredAreaM2?: number;
}

export interface BackdropTransform {
  /** Offset of the image origin in model millimetres. */
  x: number;
  y: number;
  /** Radians, clockwise in screen space. */
  rotation: number;
  /** The calibration: how many millimetres one image pixel represents. */
  mmPerPixel: number;
}

export interface Backdrop {
  assetId: string;
  opacity: number;
  transform: BackdropTransform;
}

/**
 * How a level's scale was established.
 *
 * Recorded because an uncalibrated plan looks exactly like a calibrated one --
 * the numbers are just wrong. Without this the editor cannot tell the user
 * whether the areas it is showing mean anything yet.
 */
export interface Calibration {
  method: 'reference' | 'room-area' | 'total-area' | 'manual';
  /** What it was measured against, e.g. "900 mm door" or "Kök, 12.5 m²". */
  note: string;
  /** ISO timestamp. */
  at: string;
}

/** Phase 2. Declared now so the schema does not have to change to gain stairs. */
export interface Stair {
  id: string;
  fromLevel: string;
  toLevel: string;
  path: Point[];
  width: number;
}

/** Phase 2: a hole in this level's floor, e.g. over a stairwell. */
export interface Void {
  id: string;
  polygon: Point[];
}

export interface Level {
  id: string;
  name: string;
  /** Millimetres above the project datum. */
  elevation: number;
  /** Floor-to-floor height. */
  height: number;
  backdrop?: Backdrop | null;
  /** Absent means the scale has never been established: areas are meaningless. */
  calibration?: Calibration | null;
  junctions: Junction[];
  walls: Wall[];
  openings: Opening[];
  rooms: RoomMeta[];
  stairs: Stair[];
  voids: Void[];
}

export interface PlanDocument {
  schemaVersion: number;
  units: 'mm';
  levels: Level[];
}

export function emptyLevel(id = 'lvl_0', name = 'Plan 1'): Level {
  return {
    id,
    name,
    elevation: 0,
    height: 2500,
    junctions: [],
    walls: [],
    openings: [],
    rooms: [],
    stairs: [],
    voids: [],
  };
}

export function emptyDocument(): PlanDocument {
  return { schemaVersion: SCHEMA_VERSION, units: 'mm', levels: [emptyLevel()] };
}

/** Default wall thickness in mm: a typical Swedish non-load-bearing partition. */
export const DEFAULT_WALL_THICKNESS = 120;

/** Defaults for newly placed openings, in mm. */
export const OPENING_DEFAULTS: Record<OpeningType, { width: number; sill: number; head: number }> = {
  door: { width: 900, sill: 0, head: 2100 },
  window: { width: 1200, sill: 900, head: 2100 },
  opening: { width: 1500, sill: 0, head: 2100 },
};
